import React, { useEffect, useState } from "react";
import { Grid, InputLabel, LinearProgress, MenuItem, Paper, Select, Skeleton, Box, TextField, FormControl, Button } from "@mui/material";
import { usePublicAccountController } from "./context/public-account-controller";
import { IListPublicAccountRequest } from "../../model/base/request/list-public-account-request";
import { useParams } from "react-router-dom";
import { usePublicAccountState } from "./context/public-account-context";
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { IPublicAccountResponse } from "../../model/base/response/public-account-response";
import PageHeader from "../components/shared/page-header";
import ClearIcon from '@mui/icons-material/Clear';
import DescriptionIcon from '@mui/icons-material/Description';

export function PublicAccountComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { initalData, loadData, loadSubcategories } = usePublicAccountController();
    const { categoryId, subcategoryId } = useParams();
    const { category, subcategory, categories, subcategories, publicAccount } = usePublicAccountState();
    const [paramsRequet, setParamsRequest] = useState<IListPublicAccountRequest>({ categoryId, subcategoryId, page: 0, pageSize: 10 } as IListPublicAccountRequest);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<number | ''>('');

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequet);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequet]);

    useEffect(() => {
        const onInit = async () => {
            await initalData(paramsRequet);
            setInitialLoading(false);
        };
        onInit();
    }, []);

    // Carrega subcategorias quando a categoria mudar
    useEffect(() => {
        if (selectedCategoryId) {
            loadSubcategories(selectedCategoryId as number);
            setSelectedSubcategoryId('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategoryId]);

    // Atualiza os parâmetros de request quando os filtros mudarem
    useEffect(() => {
        setParamsRequest({
            ...paramsRequet,
            categoryId: selectedCategoryId || undefined,
            subcategoryId: selectedSubcategoryId || undefined,
            page: 0
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategoryId, selectedSubcategoryId]);

    const handleClearFilters = () => {
        setParamsRequest({ categoryId, subcategoryId, page: 0, pageSize: 10 } as IListPublicAccountRequest);
        setSelectedCategoryId('');
        setSelectedSubcategoryId('');
    };

    const columns: GridColDef<IPublicAccountResponse>[] = [
        {
            field: 'description',
            headerName: 'Descrição',
            minWidth: 200,
            flex: 3,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            display: 'block',
                            width: '100%'
                        }}
                    >
                        {params.row.description}
                    </Box>
                );
            }
        },
        {
            field: 'category',
            headerName: 'Categoria',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                return params.row.category || '-';
            }
        },
        {
            field: 'subcategory',
            headerName: 'Subcategoria',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                return params.row.subcategory || '-';
            }
        },
        {
            field: 'date',
            headerName: 'Data de Publicação',
            minWidth: 130,
            flex: 1,
            valueFormatter: params => params?.value ? dayjs(params.value).format('DD/MM/YYYY') : '-',
        },
        {
            field: 'actions',
            headerName: 'Ver Documentos',
            minWidth: 150,
            flex: 1.5,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        {params.row.documents?.map((document, index) => (
                            <Button
                                key={`${params.row.id}-${index}`}
                                variant="text"
                                size="small"
                                endIcon={<DescriptionIcon />}
                                href={document.fileViewUrl}
                                target="_blank"
                                rel="noopener"
                                component="a"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minWidth: '130px',
                                    padding: '4px 12px'
                                }}
                            >
                                {document.documentType || 'Ver Arquivo'}
                            </Button>
                        ))}
                    </Box>
                );
            }
        }
    ];

    return (
        <Box>
            <PageHeader
                title="Contas Públicas"
                subtitle="Acesse dados e informações sobre a gestão pública municipal. Em conformidade com a Lei de Acesso à Informação, disponibilizamos relatórios sobre receitas, despesas, contratos, convênios e demais atos da administração."
                breadcrumbs={[
                    { label: 'Contas Públicas' },
                    ...(category && category.description ? [{ label: category.description }] : []),
                    ...(subcategory && subcategory.description ? [{ label: subcategory.description }] : []),
                ]}
            />

            {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label="Buscar por descrição"
                                    placeholder="Digite para buscar na descrição"
                                    value={paramsRequet.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequet, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        value={selectedCategoryId}
                                        label="Categoria"
                                        onChange={(e) => setSelectedCategoryId(e.target.value as number | '')}
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {categories?.map((element) => (
                                            <MenuItem value={element.id} key={element.id}>
                                                {element.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth disabled={!selectedCategoryId}>
                                    <InputLabel>Subcategoria</InputLabel>
                                    <Select
                                        value={selectedSubcategoryId}
                                        label="Subcategoria"
                                        onChange={(e) => setSelectedSubcategoryId(e.target.value as number | '')}
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {subcategories?.map((element) => (
                                            <MenuItem value={element.id} key={element.id}>
                                                {element.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={1.75}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data de"
                                        format="DD/MM/YYYY"
                                        value={paramsRequet.startDate ? dayjs(paramsRequet.startDate) : null}
                                        onChange={(e: Dayjs | null) => setParamsRequest({ ...paramsRequet, page: 0, startDate: e?.format('YYYY-MM-DD') })}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6} md={1.75}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data até"
                                        format="DD/MM/YYYY"
                                        value={paramsRequet.endDate ? dayjs(paramsRequet.endDate) : null}
                                        onChange={(e: Dayjs | null) => setParamsRequest({ ...paramsRequet, page: 0, endDate: e?.format('YYYY-MM-DD') })}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} md={1.5}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                        <DataGrid
                            key={JSON.stringify(paramsRequet)}
                            rows={publicAccount.items || []}
                            rowCount={publicAccount.totalRecords || 0}
                            columns={columns}
                            getRowId={(row) => row.id!}
                            hideFooterSelectedRowCount
                            paginationModel={{ page: paramsRequet.page, pageSize: paramsRequet.pageSize } as GridPaginationModel}
                            onPaginationModelChange={(e) => { setParamsRequest({ ...paramsRequet, page: e.page, pageSize: e.pageSize }) }}
                            pageSizeOptions={[10]}
                            paginationMode="server"
                            getRowHeight={() => 'auto'}
                            disableColumnMenu
                            slots={{
                                loadingOverlay: LinearProgress,
                            }}
                            loading={loadMoreData}
                            localeText={{
                                MuiTablePagination: {
                                    labelDisplayedRows: ({ from, to, count }) =>
                                        `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`,
                                    labelRowsPerPage: 'Linhas por página:',
                                },
                                noRowsLabel: 'Nenhum registro encontrado',
                                noResultsOverlayLabel: 'Nenhum resultado encontrado',
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiDataGrid-main': {
                                    overflow: 'hidden',
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                    overflowX: 'hidden !important',
                                },
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 'normal !important',
                                    maxHeight: 'none !important',
                                    minHeight: '52px !important',
                                    whiteSpace: 'normal',
                                    py: 1.5,
                                },
                                '& .MuiDataGrid-row': {
                                    maxHeight: 'none !important',
                                },
                            }}
                        />
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
